import styled, { keyframes } from 'styled-components'
import { ProgressFlow, ProgressFlows, RoundedCorner } from './types'

const swingIn = keyframes`
  0% {
    transform: rotateX(-100deg);
    transform-origin: top;
    opacity: 0;
  }
  100% {
    transform: rotateX(0deg);
    transform-origin: top;
    opacity: 1;
  }
`

const borderRadiusFor = (rounded: RoundedCorner) => {
  switch (rounded) {
    case 'left':
      return '25px 0 25px 25px'
    case 'right':
      return '0 25px 25px 25px'
    case 'both':
      return '25px'
    case 'bottom':
      return '0 0 25px 25px'
    default:
      return '0'
  }
}

const Container = styled.div<{ $rounded: RoundedCorner }>`
  background: #f2f2f2;
  padding: 10px;
  margin: 0 15px;
  animation: ${swingIn} 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) both;
  border-radius: ${({ $rounded }) => borderRadiusFor($rounded)};

  &:hover {
    cursor: pointer;
  }
`

const FlowName = styled.h5`
  text-align: left;
  color: #55616b;
  text-transform: capitalize;
  padding: 5px;
`

const TasksList = styled.ul`
  height: fit-content;
  margin: 0;
  padding: 0;

  @media (max-width: 800px) {
    height: auto !important;
  }
`

const TaskRow = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  list-style: none;

  p {
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.57;
    letter-spacing: 0.21px;
    padding: 0.5rem;
    text-transform: capitalize;
  }
`

const Status = styled.span<{ $complete: boolean; $mainColor?: string }>`
  padding: 5px;
  text-align: center;
  color: white;
  border-radius: 5px;
  min-width: 85px;
  font-weight: bold;
  text-decoration: none;
  background: ${({ $complete, $mainColor }) =>
    $complete
      ? $mainColor || 'var(--mainHighlight)'
      : 'var(--warningHighlight)'} !important;
`

export const convertToTitle = (unformatted = '') => {
  if (unformatted === 'IDVTDialog') {
    return 'Identity verification'
  }

  if (unformatted === 'company') {
    return 'Organisation'
  }

  return unformatted.replace(/([A-Z])/g, ' $1')
}

export interface FlowStagesProps {
  rounded: RoundedCorner
  flows: ProgressFlows
  title: string
  mainColor?: string
  onTouchStart?: (event: React.TouchEvent) => void
  onTouchEnd?: (event: React.TouchEvent) => void
}

export const FlowStages = ({
  rounded,
  flows,
  title,
  mainColor,
  onTouchStart,
  onTouchEnd,
}: FlowStagesProps) => {
  const findFlow: ProgressFlow | undefined = flows[title]
  const hasActions =
    findFlow !== undefined &&
    Array.isArray(findFlow.actions) &&
    findFlow.actions.length > 0

  return (
    <Container
      $rounded={rounded}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <FlowName>{convertToTitle(title)}</FlowName>
      {hasActions && findFlow ? (
        <TasksList>
          {findFlow.actions.map((flow, index) => (
            <TaskRow key={index}>
              <p>
                {flow.displayName || convertToTitle(flow.id)}
              </p>
              <Status $complete={flow.completed === true} $mainColor={mainColor}>
                {flow.completed === true ? 'Completed' : 'Awaiting'}
              </Status>
            </TaskRow>
          ))}
        </TasksList>
      ) : (
        <h5>All {title} actions have been completed.</h5>
      )}
    </Container>
  )
}
